import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  Checkbox,
  LoadingSkeleton,
  Table,
  Tooltip,
} from "@narmi/design_system";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import {
  shouldShowRecentlyChanged,
  type PaginatedDualApproval,
} from "byzantine/src/dbbl/businessLogic/entities/dualApprovals";
import Filters from "byzantine/src/filters";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import styles from "../DualApproval.module.scss";
import { DUAL_APPROVALS_ROUTE, DUAL_APPROVALS_PENDING_ROUTE } from "../routes";
import useDualApprovalDrawerNavigation from "../hooks/useDualApprovalDrawerNavigation";
import useDualApprovalSelection from "../hooks/useDualApprovalSelection";
import useDualApprovalActions from "../hooks/useDualApprovalActions";
import DualApprovalActionBar from "../common/DualApprovalActionBar";
import DualApprovalCheckboxCell from "../common/DualApprovalCheckboxCell";
import { getFormattedAmount } from "../utils";
import DualApprovalDrawer from "./DualApprovalDrawer";
import shouldShowDueSoon from "./utils";

type PendingTableProps = {
  dualApprovals: PaginatedDualApproval[];
  loading: boolean;
  handleRefresh: () => Promise<API.FetchDualApproval.Response | undefined>;
};

type DualApprovalTableSortConfigType = {
  column: "date" | "to" | "submittedBy" | "amount" | null;
  direction: "asc" | "desc" | null;
};

const PendingTable = ({
  dualApprovals,
  loading,
  handleRefresh,
}: PendingTableProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const navigate = useNavigate();
  const features = useUserFeatures();
  const { ach_payments_collect: achPaymentsCollectFeature } = features;

  const [sortConfig, setSortConfig] = useState<DualApprovalTableSortConfigType>(
    {
      column: "date",
      direction: "desc",
    }
  );

  const getSortableAmount = (item: PaginatedDualApproval) => {
    const parseCurrencyAmount = (value?: string | number) => {
      const parsedValue = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));

      return Number.isFinite(parsedValue) ? parsedValue : 0;
    };

    if (item.bulkPaymentId || item.bulkReversalId) {
      return achPaymentsCollectFeature
        ? parseCurrencyAmount(item.bulk?.netTotalDollarsWithSign)
        : parseCurrencyAmount(item.bulk?.netTotalDollarsWithoutSign);
    }

    if (item.nachaPassthroughSubmissionId) {
      return parseCurrencyAmount(item.dollarAmount);
    }

    if (achPaymentsCollectFeature && item.transactionType === "debit") {
      return parseCurrencyAmount(item.dollarAmount);
    }

    return achPaymentsCollectFeature
      ? -parseCurrencyAmount(item.dollarAmount)
      : parseCurrencyAmount(item.dollarAmount);
  };

  const pendingPath = `/${DUAL_APPROVALS_ROUTE}/${DUAL_APPROVALS_PENDING_ROUTE}`;

  const sortedDualApprovals = useMemo(() => {
    if (!sortConfig.column || !sortConfig.direction) {
      return dualApprovals;
    }

    const { column, direction } = sortConfig;
    const isAscending = direction === "asc";
    const data = [...dualApprovals];

    if (column === "date") {
      const scheduledACH = data
        .filter(
          (
            item
          ): item is PaginatedDualApproval & {
            schedule: NonNullable<PaginatedDualApproval["schedule"]>;
          } => Boolean(item.scheduledACHId && item.schedule?.scheduledDate)
        )
        .sort((a, b) => {
          const aTime = new Date(a.schedule.scheduledDate || 0).getTime();
          const bTime = new Date(b.schedule.scheduledDate || 0).getTime();
          return isAscending ? aTime - bTime : bTime - aTime;
        });

      const nonScheduled = data.filter(
        (item) => !item.scheduledACHId || !item.schedule?.scheduledDate
      );

      return [...nonScheduled, ...scheduledACH];
    }

    if (column === "to") {
      const bulk = data.filter(
        (item) => item.bulkPaymentId || item.bulkReversalId
      );
      const nonBulk = data
        .filter((item) => !item.bulkPaymentId && !item.bulkReversalId)
        .sort((a, b) => {
          const aName = a.recipientName || "";
          const bName = b.recipientName || "";
          return isAscending
            ? aName.localeCompare(bName)
            : bName.localeCompare(aName);
        });

      return [...bulk, ...nonBulk];
    }

    if (column === "submittedBy") {
      return data.sort((a, b) => {
        const aName = a.requesterName || "";
        const bName = b.requesterName || "";
        return isAscending
          ? aName.localeCompare(bName)
          : bName.localeCompare(aName);
      });
    }

    if (column === "amount") {
      return data.sort((a, b) =>
        isAscending
          ? getSortableAmount(a) - getSortableAmount(b)
          : getSortableAmount(b) - getSortableAmount(a)
      );
    }

    return data;
  }, [achPaymentsCollectFeature, dualApprovals, sortConfig]);

  const onFetchError = useCallback(() => {
    sendNotification({
      type: "negative",
      text: l10n.getString("error-generic"),
    });
  }, [l10n, sendNotification]);

  const {
    selectedIndex,
    activeDualApproval,
    showControls,
    isDrawerOpen,
    handleOpenDrawer,
    handleCloseDrawer,
    handleOnNext,
    handleOnPrev,
    getPathWithDrawer,
  } = useDualApprovalDrawerNavigation({
    items: sortedDualApprovals,
    basePath: pendingPath,
    loading,
    onFetchError,
  });

  const {
    selectedIds,
    selectedApprovals,
    totalSelected,
    allSelectableAreSelected,
    handleToggleCheckbox,
    handleToggleCheckboxHeader,
    deselectItem,
    setSelectedOnly,
  } = useDualApprovalSelection(sortedDualApprovals);

  const {
    isActionLoading,
    toggledDialogName,
    toggleDialog,
    handleAction,
    handleDrawerAction,
  } = useDualApprovalActions({
    selectedApprovals,
    totalSelected,
    handleRefresh,
    notificationKeys: {
      approve: "approval-notif-items-approved",
      reject: "approval-notif-items-rejected",
    },
    onSelectionReset: (failedIds) => setSelectedOnly(failedIds),
    onDeselectItem: (id) => deselectItem(id),
  });

  const calculateTotalAmount = (approvals: PaginatedDualApproval[]) => {
    const calculatedTotalAmount = approvals.reduce(
      (totalAmount, item) => totalAmount + (item.dollarAmount || 0),
      0
    );
    return Filters.currency(calculatedTotalAmount as Dollars, {
      hasDecimal: true,
    });
  };

  const onDrawerAction = (action: "approve" | "reject") =>
    handleDrawerAction({
      action,
      activeDualApproval,
      items: sortedDualApprovals,
      selectedIndex,
      getPathWithDrawer,
      navigate,
      handleCloseDrawer,
    });

  const sortApprovals = (column: DualApprovalTableSortConfigType["column"]) => {
    const isSameColumn = sortConfig.column === column;
    const direction =
      isSameColumn && sortConfig.direction === "asc" ? "desc" : "asc";

    setSortConfig({ column, direction });
  };

  const ArrowIcon = ({
    column,
  }: {
    column: DualApprovalTableSortConfigType["column"];
  }) => {
    const { direction, column: configuredColumn } = sortConfig;
    const isSameColumn = configuredColumn === column;

    const getIconClass = () => {
      if (!isSameColumn || direction === "asc") {
        return "narmi-icon-arrow-up";
      }

      return "narmi-icon-arrow-down";
    };

    return (
      <span className={`fontSize--xs margin--left--xxs ${getIconClass()}`} />
    );
  };

  const renderRow = (item: PaginatedDualApproval) => {
    const isDueSoon = shouldShowDueSoon(item);
    const isRecentlyChanged = shouldShowRecentlyChanged({
      createdAt: item.createdAt,
      recipientUpdatedAt: item.recipient?.lastUpdatedAt,
      achId: item.achId,
      fedNowTransactionId: item.fedNowTransactionId,
      scheduledACHId: item.scheduledACHId,
    });

    const getScheduledDate = () => {
      if (item.scheduledACHId && item.schedule?.scheduledDate) {
        return Filters.longMonthDay(item.schedule.scheduledDate);
      }
      if (
        item.bulkPaymentId &&
        (item.bulk?.noYearDateRange || item.bulk?.dateRange)
      ) {
        return item.bulk.noYearDateRange || item.bulk.dateRange;
      }

      return l10n.getString("pending-table-one-time-payment");
    };

    const getFrequency = () => {
      if (item.scheduledACHId && item.schedule?.frequency) {
        return item.schedule.frequency;
      }

      return l10n.getString("table-cell-frequency-one-time", {}, "One-time");
    };

    const getSubmittedBy = () =>
      item.isOwnRequest ? l10n.getString("label-you") : item.requesterName;

    return (
      <Table.Row
        key={`table-row__${item.id}`}
        onRowClick={() => handleOpenDrawer(item.id)}
      >
        <Table.Cell>
          <DualApprovalCheckboxCell
            isOwnRequest={Boolean(item.isOwnRequest)}
            isChecked={Boolean(selectedIds[item.id])}
            onChange={() => handleToggleCheckbox(item.id)}
          />
        </Table.Cell>
        <Table.Cell>
          {getScheduledDate()}
          {isDueSoon ? (
            <Tooltip maxWidth="250px" text={l10n.getString("due-soon-tooltip")}>
              <span className="fontSize--m narmi-icon-alert-circle fontColor--error margin--left--xs" />
            </Tooltip>
          ) : null}
        </Table.Cell>
        <Table.Cell>
          {item.recipientName}
          {isRecentlyChanged ? (
            <Tooltip
              maxWidth="210px"
              text={l10n.getString("pending-drawer-recently-changed-copy")}
            >
              <span className="fontSize--m narmi-icon-alert-circle fontColor--error margin--left--xs" />
            </Tooltip>
          ) : null}
        </Table.Cell>
        <Table.Cell>
          {getFormattedAmount(item, achPaymentsCollectFeature)}
        </Table.Cell>
        <Table.Cell>{getSubmittedBy()}</Table.Cell>
        <Table.Cell>{item.displayType}</Table.Cell>
        <Table.Cell>{getFrequency()}</Table.Cell>
        <Table.Cell>
          <div className={styles.viewCtaContainer}>
            <span className={styles.viewCtaText}>
              {l10n.getString("button-view", {}, "View")}
            </span>
            <span className="fontSize--m narmi-icon-chevron-right" />
          </div>
        </Table.Cell>
      </Table.Row>
    );
  };

  if (loading && !isDrawerOpen) {
    return <LoadingSkeleton isLoading />;
  }

  if (sortedDualApprovals.length === 0 && !loading) {
    return (
      <div className={styles.emptyState}>
        {l10n.getString(
          "heading-no-pending-approvals",
          {},
          "No pending approvals"
        )}
      </div>
    );
  }

  return (
    <>
      <div className={styles.tableScrollWrapper}>
        <Table
          colVisibility={[
            "*", // checkbox
            "*", // scheduled date
            "*", // to/from
            "*", // amount
            "m", // submitted by only on >M screens
            "l", // type only on >L screens
            "l", // frequency only on >L screens
            "m", // view cta only on >M screens
          ]}
          colLayout={{
            s: "auto 1fr 1fr 1fr",
            m: "auto 1.5fr 1fr 1fr 1fr auto",
            l: "auto 1.5fr 1fr .75fr 1fr .75fr 1fr auto",
          }}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                <Checkbox
                  checked={allSelectableAreSelected}
                  onChange={() =>
                    handleToggleCheckboxHeader(!allSelectableAreSelected)
                  }
                />
              </Table.HeaderCell>
              <Table.HeaderCell onClick={() => sortApprovals("date")}>
                {l10n.getString("th-scheduled-date", {}, "Scheduled Date")}
                <ArrowIcon column="date" />
              </Table.HeaderCell>
              <Table.HeaderCell onClick={() => sortApprovals("to")}>
                {achPaymentsCollectFeature
                  ? `${l10n.getString("th-to", {}, "To")}/${l10n.getString(
                      "th-from",
                      {},
                      "From"
                    )}`
                  : l10n.getString("th-to", {}, "To")}
                <ArrowIcon column="to" />
              </Table.HeaderCell>
              <Table.HeaderCell onClick={() => sortApprovals("amount")}>
                {achPaymentsCollectFeature
                  ? l10n.getString("label-net-total", {}, "Net Total")
                  : l10n.getString("th-amount", {}, "Amount")}
                <ArrowIcon column="amount" />
              </Table.HeaderCell>
              <Table.HeaderCell onClick={() => sortApprovals("submittedBy")}>
                {l10n.getString("th-submitted-by", {}, "Submitted By")}
                <ArrowIcon column="submittedBy" />
              </Table.HeaderCell>
              <Table.HeaderCell>
                {l10n.getString("th-type", {}, "Type")}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {l10n.getString("th-frequency", {}, "Frequency")}
              </Table.HeaderCell>
              <Table.HeaderCell>{""}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>{sortedDualApprovals.map(renderRow)}</Table.Body>
        </Table>
      </div>
      <DualApprovalDrawer
        isOpen={isDrawerOpen}
        showControls={showControls}
        handleClose={handleCloseDrawer}
        handleOnNext={handleOnNext}
        handleOnPrev={handleOnPrev}
        onFetchError={onFetchError}
        handleApprove={() => onDrawerAction("approve")}
        handleReject={() => onDrawerAction("reject")}
        isActionLoading={isActionLoading}
        dualApproval={isDrawerOpen ? activeDualApproval : null}
        drawerType="pending"
      />
      <DualApprovalActionBar
        totalSelected={totalSelected}
        isActionLoading={isActionLoading}
        toggledDialogName={toggledDialogName}
        toggleDialog={toggleDialog}
        handleAction={handleAction}
        handleDeselectAll={() => handleToggleCheckboxHeader(false)}
        rejectTestId="snackbar-reject-approval"
        approveTestId="snackbar-approve-approval"
      >
        <span className="fontColor--primary fontWeight--semibold">
          {`${totalSelected} ${l10n.getString("snackbar-payment-selected", {
            totalSelected,
          })}`}
        </span>
        <span className="fontColor--secondary">
          {l10n.getString("payment-total", {
            amount: calculateTotalAmount(selectedApprovals),
          })}
        </span>
      </DualApprovalActionBar>
    </>
  );
};

export default PendingTable;
